import { reverseComplement } from "@/lib/tool-kit/biology-genetics/iupac";

/**
 * Melting temperature of a DNA oligonucleotide, three ways.
 *
 *  1. Wallace rule (Wallace et al. 1979; Thein & Wallace 1986): Tm = 2·(A + T) + 4·(G + C).
 *  2. Salt-adjusted GC formula (Marmur & Doty 1962; Schildkraut & Lifson 1965):
 *     Tm = 81.5 + 16.6·log10[Na⁺] + 0.41·(%GC) − 675 / N.
 *  3. Nearest-neighbour thermodynamics with the SantaLucia (1998, PNAS 95:1460) unified
 *     parameters: Tm = ΔH / (ΔS + R·ln(C/x)) − 273.15, with the sodium correction
 *     ΔS[Na⁺] = ΔS[1 M] + 0.368·(N − 1)·ln[Na⁺] from the same paper.
 *
 * Other ions are converted to a sodium equivalent as von Ahsen et al. (2001, Clin Chem
 * 47:1956) do: [Na⁺]eq = [Na⁺] + [K⁺] + [Tris]/2 + 120·√([Mg²⁺] − [dNTPs]), in mmol/L,
 * and the Mg²⁺ term is dropped when the dNTPs bind all of it. This is what Primer3 and
 * Biopython compute, and the unit tests pin the output to both.
 */

export interface Conditions {
  /** Total oligonucleotide strand concentration, nmol/L. */
  oligoNm: number;
  naMm: number;
  kMm: number;
  trisMm: number;
  mgMm: number;
  dntpMm: number;
}

export const DEFAULT_CONDITIONS: Conditions = {
  oligoNm: 250,
  naMm: 50,
  kMm: 0,
  trisMm: 0,
  mgMm: 0,
  dntpMm: 0,
};

const GAS_CONSTANT = 1.987; // cal / (K·mol), as in SantaLucia (1998)
const KELVIN = 273.15;
const BODY_TEMPERATURE = 310.15; // 37 °C in kelvin

/** ΔH (kcal/mol) and ΔS (cal/(K·mol)) in 1 M NaCl — SantaLucia (1998), Table 2. */
const NEAREST_NEIGHBOUR: Readonly<Record<string, readonly [number, number]>> = {
  "AA/TT": [-7.9, -22.2],
  "AT/TA": [-7.2, -20.4],
  "TA/AT": [-7.2, -21.3],
  "CA/GT": [-8.5, -22.7],
  "GT/CA": [-8.4, -22.4],
  "CT/GA": [-7.8, -21.0],
  "GA/CT": [-8.2, -22.2],
  "CG/GC": [-10.6, -27.2],
  "GC/CG": [-9.8, -24.4],
  "GG/CC": [-8.0, -19.9],
};
const INIT_GC: readonly [number, number] = [0.1, -2.8];
const INIT_AT: readonly [number, number] = [2.3, 4.1];
const SYMMETRY: readonly [number, number] = [0, -1.4];

const COMPLEMENT: Readonly<Record<string, string>> = {
  A: "T",
  C: "G",
  G: "C",
  T: "A",
};
const DNA_ONLY = /^[ACGT]+$/;

/** Upper-case ACGT with U read as T, or null when anything else is present. */
export function normalizePrimer(raw: string): string | null {
  const cleaned = raw.replace(/\s+/g, "").toUpperCase().replaceAll("U", "T");
  return DNA_ONLY.test(cleaned) ? cleaned : null;
}

export function gcPercent(sequence: string): number {
  let gc = 0;
  for (const base of sequence) {
    if (base === "G" || base === "C") {
      gc += 1;
    }
  }
  return (gc / sequence.length) * 100;
}

/** The sodium-equivalent monovalent concentration, mmol/L. */
export function monovalentEquivalent(c: Conditions): number {
  const monovalent = c.naMm + c.kMm + c.trisMm / 2;
  const freeMg = c.mgMm - c.dntpMm;
  return freeMg > 0 && c.mgMm > 0
    ? monovalent + 120 * Math.sqrt(freeMg)
    : monovalent;
}

export function tmWallace(sequence: string): number {
  let tm = 0;
  for (const base of sequence) {
    tm += base === "G" || base === "C" ? 4 : 2;
  }
  return tm;
}

/** Salt-adjusted GC-content formula; null when there is no monovalent salt. */
export function tmSaltAdjusted(sequence: string, c: Conditions): number | null {
  const mon = monovalentEquivalent(c) / 1000;
  if (!(mon > 0)) {
    return null;
  }
  return (
    81.5 +
    16.6 * Math.log10(mon) +
    0.41 * gcPercent(sequence) -
    675 / sequence.length
  );
}

export function isSelfComplementary(sequence: string): boolean {
  return sequence.length > 1 && reverseComplement(sequence) === sequence;
}

export interface NearestNeighbourResult {
  tm: number;
  /** kcal/mol, 1 M NaCl terms plus initiation (and symmetry). */
  deltaH: number;
  /** cal/(K·mol), after the salt correction. */
  deltaS: number;
  /** ΔG at 37 °C in kcal/mol: ΔH − T·ΔS. */
  deltaG37: number;
  selfComplementary: boolean;
}

function neighbourParameters(pair: string): readonly [number, number] {
  const direct =
    NEAREST_NEIGHBOUR[`${pair}/${COMPLEMENT[pair[0]]}${COMPLEMENT[pair[1]]}`];
  if (direct) {
    return direct;
  }
  // The same duplex step read from the other strand: XY/X'Y' equals Y'X'/YX.
  const flipped = `${COMPLEMENT[pair[1]]}${COMPLEMENT[pair[0]]}/${pair[1]}${pair[0]}`;
  return NEAREST_NEIGHBOUR[flipped] ?? [0, 0];
}

/** Nearest-neighbour Tm; null when the sequence is shorter than 2 bases or there is no salt. */
export function tmNearestNeighbour(
  sequence: string,
  c: Conditions
): NearestNeighbourResult | null {
  const mon = monovalentEquivalent(c) / 1000;
  if (sequence.length < 2 || !(mon > 0) || !(c.oligoNm > 0)) {
    return null;
  }
  let deltaH = 0;
  let deltaS = 0;
  for (let i = 0; i < sequence.length - 1; i++) {
    const [h, s] = neighbourParameters(sequence.slice(i, i + 2));
    deltaH += h;
    deltaS += s;
  }
  for (const end of [sequence[0], sequence.at(-1)]) {
    const [h, s] = end === "G" || end === "C" ? INIT_GC : INIT_AT;
    deltaH += h;
    deltaS += s;
  }
  const selfComplementary = isSelfComplementary(sequence);
  if (selfComplementary) {
    deltaH += SYMMETRY[0];
    deltaS += SYMMETRY[1];
  }
  deltaS += 0.368 * (sequence.length - 1) * Math.log(mon);
  const strands = selfComplementary ? c.oligoNm * 1e-9 : (c.oligoNm * 1e-9) / 4;
  const tm =
    (1000 * deltaH) / (deltaS + GAS_CONSTANT * Math.log(strands)) - KELVIN;
  return {
    tm,
    deltaH,
    deltaS,
    deltaG37: deltaH - (BODY_TEMPERATURE * deltaS) / 1000,
    selfComplementary,
  };
}

export interface PrimerCheck {
  /** 18–30 bases is the usual PCR primer length. */
  lengthOk: boolean;
  /** 40–60 % GC. */
  gcOk: boolean;
  /** No more than 3 G/C among the last 5 bases. */
  clampOk: boolean;
  /** Longest run of one base. */
  longestRun: number;
  runOk: boolean;
  selfComplementary: boolean;
}

function longestRun(sequence: string): number {
  let best = 1;
  let current = 1;
  for (let i = 1; i < sequence.length; i++) {
    current = sequence[i] === sequence[i - 1] ? current + 1 : 1;
    best = Math.max(best, current);
  }
  return sequence.length === 0 ? 0 : best;
}

export function checkPrimer(sequence: string): PrimerCheck {
  const gc = gcPercent(sequence);
  const tail = sequence.slice(-5);
  const clampGc = [...tail].filter(
    (base) => base === "G" || base === "C"
  ).length;
  const run = longestRun(sequence);
  return {
    lengthOk: sequence.length >= 18 && sequence.length <= 30,
    gcOk: gc >= 40 && gc <= 60,
    clampOk: clampGc <= 3,
    longestRun: run,
    runOk: run <= 4,
    selfComplementary: isSelfComplementary(sequence),
  };
}

/**
 * Do the last `span` bases at the 3′ end of `a` pair with some stretch of `b`? A
 * 3′-anchored match is what lets a polymerase extend a primer-dimer.
 */
export function threePrimeMatch(a: string, b: string, span = 5): boolean {
  if (a.length < span || b.length < span) {
    return false;
  }
  return b.includes(reverseComplement(a.slice(-span)));
}
